import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_RAW = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND = BACKEND_RAW.replace(/\/+$/, ""); // sondaki slash'ları temizle (prod proxy çift-slash bug'ı)
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tk_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Detect mis-routed API responses (e.g. SPA index.html instead of JSON)
api.interceptors.response.use(
  (response) => {
    const ct = (response.headers?.["content-type"] || "").toLowerCase();
    const data = response.data;
    if (ct.includes("text/html") || (typeof data === "string" && data.trim().startsWith("<"))) {
      const err = new Error("API HTML döndü — backend URL yanlış yapılandırılmış olabilir");
      err.response = response;
      throw err;
    }
    return response;
  },
  (error) => Promise.reject(error),
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authed
  const [checking, setChecking] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("tk_token");
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("tk_token");
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("tk_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("tk_token");
    setUser(null);
  };

  const changePassword = async (newPassword) => {
    await api.post("/auth/change-password", { new_password: newPassword });
    const updated = { ...user, must_change_password: false };
    setUser(updated);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, changePassword, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function fmtEUR(n) {
  if (n == null || isNaN(n)) n = 0;
  // Yeni format: "1.490 €" (sembol sağda, ondalık YOK)
  const num = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
  return `${num} €`;
}
export function fmtTRY(n) {
  if (n == null || isNaN(n)) n = 0;
  // Yeni format: "1.490 ₺" (sembol sağda, ondalık YOK)
  const num = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
  return `${num} ₺`;
}

// Turkish-correct uppercase (preserves i→İ, ı→I)
export function TU(s) {
  return (s == null ? "" : String(s)).toLocaleUpperCase("tr-TR");
}

// Format ship-style datetime "2026-08-22 15:00" or ISO → "22-Ağu'26, Cts | 15:00"
const TR_MONTHS_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const TR_DAYS_SHORT = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cts"];
export function fmtShipDate(s) {
  if (!s) return "";
  // Accept "YYYY-MM-DD HH:MM" or full ISO
  const norm = String(s).includes("T") ? s : String(s).replace(" ", "T");
  const d = new Date(norm);
  if (isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = TR_MONTHS_SHORT[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  const dow = TR_DAYS_SHORT[d.getDay()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mon}'${yy}, ${dow} | ${hh}:${mm}`;
}

// Hedef tarih: 22 Ağustos 2026, 15:00 Türkiye saati (UTC+3)
export const TRIP_TARGET = new Date("2026-08-22T15:00:00+03:00");

// Compute days/hours/minutes/seconds remaining until target
export function getTripCountdown(now = new Date()) {
  const diffMs = TRIP_TARGET.getTime() - now.getTime();
  if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / (60 * 60 * 24));
  const hours = Math.floor((totalSec % (60 * 60 * 24)) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { days, hours, minutes, seconds, ended: false };
}
